/* Smart Panels / Horizon: independent calculator, native quantity control preserved. */
(() => {
  if (window.__spPanelCalculatorLoaded) return;
  window.__spPanelCalculatorLoaded = true;

  const panels = () => document.querySelectorAll('.sp-panel-calculator[data-sp-panel]');
  const getInput = (panel) => panel.querySelector('quantity-selector-component input[name="quantity"]');
  const getSelector = (panel) => panel.querySelector('quantity-selector-component');
  const getVariantId = (panel) => panel.closest('product-form-component')?.querySelector('form input[name="id"]')?.value || '';
  const safeCoverage = (panel) => {
    const variantId = getVariantId(panel);
    const coverage = Number(panel._spVariants?.[variantId]);
    return Number.isFinite(coverage) && coverage > 0 ? coverage : 0;
  };
  const readQty = (panel) => Math.max(1, parseInt(getInput(panel)?.value || '1', 10) || 1);

  function updateCoverage(panel) {
    const current = panel.querySelector('[data-sp-coverage]');
    const display = panel.querySelector('[data-sp-covered]');
    if (!current || !display) return;
    const area = safeCoverage(panel);
    current.textContent = (readQty(panel) * area).toFixed(2);
    display.hidden = area <= 0;
  }

  function validQuantity(panel, requested) {
    const input = getInput(panel);
    const component = getSelector(panel);
    if (!input || input.disabled) return { quantity: null, reason: 'This variant is currently unavailable.' };
    const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
    const step = Math.max(1, parseInt(input.step || '1', 10) || 1);
    const quantity = min + Math.ceil(Math.max(0, requested - min) / step) * step;
    const max = typeof component?.getEffectiveMax === 'function'
      ? component.getEffectiveMax()
      : input.max ? parseInt(input.max, 10) : null;
    if (max !== null && Number.isFinite(max) && quantity > max) {
      return { quantity: null, reason: 'The calculated quantity exceeds the available limit. Please contact us for a larger order.' };
    }
    return { quantity, reason: '' };
  }

  function setQuantity(panel, next) {
    const selector = getSelector(panel);
    const input = getInput(panel);
    if (!selector || !input) return;
    if (String(next) === input.value) return;
    if (typeof selector.setValue === 'function') selector.setValue(String(next));
    else input.value = String(next);
    if (typeof selector.onQuantityChange === 'function') selector.onQuantityChange();
    else input.dispatchEvent(new Event('change', { bubbles: true }));
    if (typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
    updateCoverage(panel);
  }

  function calculate(panel) {
    const widthNode = panel.querySelector('[data-sp-width]');
    const heightNode = panel.querySelector('[data-sp-height]');
    const result = panel.querySelector('[data-sp-result]');
    const warning = panel.querySelector('[data-sp-warning]');
    if (!widthNode || !heightNode || !result || !warning) return;
    const width = Number(widthNode.value);
    const height = Number(heightNode.value);
    const areaPerPanel = safeCoverage(panel);
    warning.hidden = true;
    result.hidden = true;
    if (widthNode.value === '' || heightNode.value === '' || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || areaPerPanel <= 0) {
      if (areaPerPanel <= 0 && widthNode.value && heightNode.value) {
        warning.textContent = 'Panel coverage is missing for this variant.';
        warning.hidden = false;
      }
      return;
    }
    const wallArea = width * height;
    const required = Math.ceil((wallArea * 1.1) / areaPerPanel);
    if (!Number.isFinite(required) || required < 1) return;
    const quantityCheck = validQuantity(panel, required);
    panel.querySelector('[data-sp-count]').textContent = String(quantityCheck.quantity ?? required);
    panel.querySelector('[data-sp-area]').textContent = wallArea.toFixed(1);
    result.hidden = false;
    if (quantityCheck.reason) {
      warning.textContent = quantityCheck.reason;
      warning.hidden = false;
      return;
    }
    setQuantity(panel, quantityCheck.quantity);
  }

  function syncVariant(panel) {
    const id = getVariantId(panel);
    if (id === panel.dataset.spActiveVariant) return;
    panel.dataset.spActiveVariant = id;
    updateCoverage(panel);
    calculate(panel);
  }

  function init(panel) {
    if (panel.dataset.spCalculatorReady === 'true') return;
    const dataNode = panel.querySelector('[data-sp-variant-coverage]');
    if (!dataNode || !getInput(panel)) return;
    try {
      panel._spVariants = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('Smart Panels calculator: invalid variant coverage data', error);
      return;
    }
    panel.dataset.spCalculatorReady = 'true';
    const toggle = panel.querySelector('[data-sp-toggle]');
    const fields = panel.querySelector('[data-sp-fields]');
    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      fields.hidden = !open;
      if (open) panel.querySelector('[data-sp-width]')?.focus();
    });
    panel.querySelector('[data-sp-width]')?.addEventListener('input', () => calculate(panel));
    panel.querySelector('[data-sp-height]')?.addEventListener('input', () => calculate(panel));
    getInput(panel)?.addEventListener('change', () => updateCoverage(panel));
    getInput(panel)?.addEventListener('input', () => updateCoverage(panel));
    syncVariant(panel);
  }
  const initAll = () => panels().forEach(init);

  // This Horizon event is emitted by the native quantity selector's +/- controls.
  document.addEventListener('quantity-selector:update', (event) => {
    const panel = event.target.closest?.('.sp-panel-calculator[data-sp-panel]');
    if (panel) updateCoverage(panel);
  });

  // Product forms resolve a variant via an asynchronous section fetch; wait for it.
  document.addEventListener('shopify:product:select', (event) => {
    const target = event.target;
    const section = target?.closest?.('.shopify-section, dialog, product-card');
    if (!section) return;
    const refresh = () => {
      section.querySelectorAll('.sp-panel-calculator[data-sp-panel]').forEach((panel) => {
        init(panel);
        syncVariant(panel);
      });
    };
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => {
        // Horizon updates form data using the same promise, sometimes after a morph.
        setTimeout(refresh, 0);
        setTimeout(refresh, 150);
      }).catch(() => {});
    } else setTimeout(refresh, 100);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();

  // When Horizon morphs its buying buttons, newly inserted controls need listeners.
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1))) initAll();
  });
  const watch = () => observer.observe(document.body, { childList: true, subtree: true });
  if (document.body) watch();
  else document.addEventListener('DOMContentLoaded', watch, { once: true });
})();
